/**
 * Rewrites a persona portrait URL to its pre-generated thumbnail variant.
 *
 * Backfill: `scripts/tools/generate_portrait_thumbnails.py` writes
 * `<name>-t64.webp` / `<name>-t128.webp` / `<name>-t256.webp` /
 * `<name>-t768.webp` beside every
 * `persona-images/<name>.png` object in the `fairgen-twins-profiles-*`
 * buckets (docs/plans/analyst-ranking-quotes-band-fixes.md, Step 5b finding
 * 7). This util is the client-side half of that convention: no DB column,
 * no backend change, just a deterministic suffix rewrite at render time.
 *
 * Only URLs matching `storage.googleapis.com/fairgen-twins-profiles-<suffix>`,
 * `/persona-images/<name>.png` are rewritten. Anything else (other hosts, non-PNG extensions, or an
 * `undefined` url) passes through unchanged — callers should always pair
 * this with an `onError` fallback to the original url in case the
 * thumbnail variant hasn't been backfilled yet for that persona.
 *
 * `768` is the large-portrait variant (quality 85, added
 * docs/plans/2026-09-18-open-end-slide-featured-polish-plan.md step 3) and is
 * the newest of the four, so a caller asking for it should degrade through
 * `256` before the original rather than straight to the ~2 MB PNG.
 */
const PERSONA_IMAGE_URL_PATTERN =
  /^(https:\/\/storage\.googleapis\.com\/fairgen-twins-profiles-[^/]+\/persona-images\/)([^/]+)\.png$/

export function personaThumbUrl(url: string | undefined, size: 64 | 128 | 256 | 768): string | undefined {
  if (!url) {
    return url
  }

  const match = PERSONA_IMAGE_URL_PATTERN.exec(url)
  if (!match) {
    return url
  }

  const [, prefix, name] = match
  return `${prefix}${name}-t${size}.webp`
}
